import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster } from "sonner";
import {
  ArrowRight, ArrowUpRight, Lock, Calendar, LayoutDashboard,
  Check, ShieldCheck,
} from "lucide-react";
import SiteHeader from "../components/gridvest/SiteHeader";
import Footer from "../components/gridvest/Footer";

export default function PilotPage() {
  return (
    <div className="min-h-screen bg-[#07111F] text-[#F8FAFC]" data-testid="pilot-page">
      <SiteHeader active="pilot" />

      <main className="relative">
        <div className="gv-grid-bg absolute inset-x-0 top-0 h-[520px]" aria-hidden />
        <div className="gv-noise" aria-hidden />

        {/* HERO */}
        <section className="relative mx-auto max-w-5xl px-6 pb-20 pt-16 text-center lg:px-8 lg:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mb-6 font-display text-[12px] font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]">
              Owner-First Infrastructure Platform
            </p>
            <h1 className="font-display text-[40px] font-extrabold leading-[0.98] tracking-[-0.05em] text-[#F8FAFC] sm:text-5xl lg:text-[72px]">
              Infrastructure agreements
              <br className="hidden sm:block" /> shouldn't disappear
              <br className="hidden sm:block" />
              <span className="text-[#94A3B8]"> into disconnected systems.</span>
            </h1>
            <p className="mx-auto mt-7 max-w-2xl font-body text-base leading-[1.75] text-[#CBD5E1] sm:text-lg">
              InfraYield helps cell tower and billboard asset owners organize leases,
              track renewals, maintain document visibility, and prepare for long-term
              infrastructure ownership continuity.
            </p>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/walkthrough"
                className="group inline-flex items-center gap-2 rounded-md bg-[#22C55E] px-6 py-3.5 font-body text-sm font-semibold text-[#07111F] transition-all hover:bg-[#22C55E]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
                data-testid="pilot-cta-walkthrough"
              >
                Request Private Walkthrough
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-md border border-[#334155] bg-transparent px-6 py-3.5 font-body text-sm font-semibold text-[#F8FAFC] transition-colors hover:border-[#38BDF8]/60 hover:bg-[#0F1B2D] focus:outline-none focus-visible:border-[#38BDF8] focus-visible:ring-2 focus-visible:ring-[#38BDF8]/30"
                data-testid="pilot-cta-dashboard"
              >
                Preview the Private Dashboard
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>

            <p className="mx-auto mt-8 max-w-2xl font-body text-xs leading-relaxed text-[#64748B]">
              Investment opportunities, if offered, will only be made through compliant
              securities structures and official offering documents.
            </p>
          </motion.div>
        </section>

        {/* CAPABILITY CARDS */}
        <section className="relative mx-auto max-w-7xl px-6 pb-10 lg:px-8">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              {
                icon: Lock,
                title: "Lease Vault",
                text: "Store telecom, billboard, rooftop, and infrastructure agreements in one organized place.",
              },
              {
                icon: Calendar,
                title: "Renewal Tracking",
                text: "Track renewal windows, expiration dates, amendments, and key infrastructure deadlines.",
              },
              {
                icon: LayoutDashboard,
                title: "Owner Dashboard",
                text: "Each pilot owner gets a private login and a clear infrastructure asset view.",
              },
            ].map((c, i) => (
              <motion.article
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="group relative overflow-hidden rounded-2xl border border-[#1E293B] bg-[#0F1B2D] p-7 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#38BDF8]/40"
                data-testid={`pilot-card-${i}`}
              >
                <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-lg border border-[#1E293B] bg-[#07111F] text-[#22C55E]">
                  <c.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#F8FAFC]">
                  {c.title}
                </h3>
                <p className="mt-3 font-body text-[15px] leading-relaxed text-[#94A3B8]">
                  {c.text}
                </p>
                <span className="absolute right-5 top-5 font-display text-xs font-medium text-[#334155]">
                  0{i + 1}
                </span>
              </motion.article>
            ))}
          </div>
        </section>

        {/* PRIVATE PILOT FORMING */}
        <PilotForming />

        {/* DASHBOARD TEASER LINK */}
        <DashboardTeaser />
      </main>

      <Footer />
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: { background: "#0F1B2D", color: "#F8FAFC", border: "1px solid #1E293B" },
        }}
      />
    </div>
  );
}

/* ---------- Private Pilot Forming section ---------- */
function PilotForming() {
  return (
    <section id="pilot" className="relative mx-auto max-w-5xl px-6 py-24 lg:px-8 lg:py-28">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="overflow-hidden rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-8 sm:p-12"
      >
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#22C55E]/30 bg-[#22C55E]/10 px-3 py-1.5 font-display text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#22C55E]">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#22C55E]" />
              Now Forming · Limited
            </span>
            <h2 className="mt-5 font-display text-3xl font-bold leading-[1.05] tracking-[-0.04em] text-[#F8FAFC] sm:text-5xl">
              Private owner pilot
              <br className="hidden sm:block" /> now forming.
            </h2>
            <p className="mt-6 max-w-xl font-body text-base leading-[1.75] text-[#CBD5E1] sm:text-lg">
              InfraYield is currently meeting with a small number of cell tower,
              billboard, and rooftop infrastructure owners to demonstrate our
              private dashboard and collect feedback before broader access opens.
            </p>

            <ul className="mt-7 space-y-2.5">
              {[
                "Private walkthrough scheduled with our team",
                "Sample asset profiles & lease vault preview",
                "Renewal timeline tailored to your portfolio",
                "Confidential — no public lease upload required",
              ].map((it) => (
                <li
                  key={it}
                  className="flex items-center gap-3 font-body text-sm text-[#CBD5E1]"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#22C55E]/40 bg-[#22C55E]/10">
                    <Check className="h-3 w-3 text-[#22C55E]" />
                  </span>
                  {it}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-[#1E293B] bg-[#07111F]/60 p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#1E293B] bg-[#0F1B2D] text-[#22C55E]">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-display text-sm font-semibold text-[#F8FAFC]">
                    Confidential intake
                  </p>
                  <p className="font-body text-xs text-[#94A3B8]">
                    Not shared publicly. Reviewed by our team.
                  </p>
                </div>
              </div>

              <p className="mt-6 font-body text-sm leading-relaxed text-[#CBD5E1]">
                Tell us about your portfolio on the private walkthrough page. A
                10-minute conversation, scheduled at your convenience.
              </p>

              <Link
                to="/walkthrough"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#22C55E] px-5 py-3.5 font-body text-sm font-semibold text-[#07111F] transition-colors hover:bg-[#22C55E]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
                data-testid="pilot-walkthrough-cta"
              >
                Request Private Walkthrough
                <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-4 text-center font-body text-[11px] text-[#64748B]">
                Pricing tiers will be introduced after the pilot.
              </p>

              <div className="mt-6 border-t border-[#1E293B] pt-5 text-center">
                <p className="font-body text-xs text-[#94A3B8]">
                  Prefer email?{" "}
                  <a
                    href="mailto:hello@infrayield.co"
                    className="font-semibold text-[#F8FAFC] underline underline-offset-4 decoration-[#38BDF8]/60 hover:decoration-[#F8FAFC]"
                  >
                    hello@infrayield.co
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ---------- Dashboard teaser link (creative) ---------- */
function DashboardTeaser() {
  return (
    <section className="relative mx-auto max-w-5xl px-6 pb-28 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
      >
        <Link
          to="/dashboard"
          className="group relative block overflow-hidden rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-8 transition-all hover:border-[#38BDF8]/40 sm:p-12"
          data-testid="pilot-dashboard-link"
        >
          {/* Mock dashboard chrome decoration */}
          <div className="pointer-events-none absolute -right-12 -top-12 hidden h-72 w-72 rotate-12 rounded-2xl border border-[#1E293B] bg-[#07111F] opacity-50 transition-opacity group-hover:opacity-80 sm:block" aria-hidden>
            <div className="border-b border-[#1E293B] bg-[#0F1B2D] px-4 py-2.5">
              <div className="flex gap-1.5">
                <span className="h-2 w-2 rounded-full bg-[#1E293B]" />
                <span className="h-2 w-2 rounded-full bg-[#1E293B]" />
                <span className="h-2 w-2 rounded-full bg-[#1E293B]" />
              </div>
            </div>
            <div className="space-y-2 p-4">
              <div className="h-2 w-3/4 rounded-full bg-[#1E293B]" />
              <div className="h-2 w-1/2 rounded-full bg-[#1E293B]" />
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="h-12 rounded-lg bg-[#0F1B2D]" />
                <div className="h-12 rounded-lg bg-[#0F1B2D]" />
                <div className="h-12 rounded-lg bg-[#0F1B2D]" />
                <div className="h-12 rounded-lg bg-[#0F1B2D]" />
              </div>
            </div>
          </div>

          <div className="relative max-w-xl">
            <p className="mb-3 font-display text-[12px] font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]">
              Walkthrough Preview
            </p>
            <h3 className="font-display text-2xl font-bold tracking-[-0.03em] text-[#F8FAFC] sm:text-3xl">
              Preview the private owner dashboard.
            </h3>
            <p className="mt-4 font-body text-[15px] leading-[1.7] text-[#94A3B8]">
              A look at the controlled walkthrough we use with pilot owners — asset
              profiles, lease vault, renewal timeline, and the owner summary report.
              Sample data only.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 font-body text-sm font-semibold text-[#F8FAFC] underline-offset-4 group-hover:underline">
              Open the walkthrough
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </span>
          </div>
        </Link>
      </motion.div>
    </section>
  );
}
